import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Activity, FileText, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI } from '@/lib/api';
import { fehlertext } from '@/pages/ErnaehrungPage';

/* Körperanalyse (InBody, 11.10.2026): Karte am Mitglied (CRM: mit Handeingabe und Löschen; Trainer-Webseite: nur
   lesen) und der Schalter für die Leitung. Alle Zahlen, Sätze und Vermerke kommen fertig vom Server
   (backend/ernaehrung/koerperanalyse.py) — hier wird nichts gerechnet. Regeln: docs/koerperanalyse.md.
   Der Grundumsatz der Waage steht NEBEN dem gerechneten; in die Bedarfsrechnung geht er nicht.
   Kontrollmessungen (Check-Up, 11.10.2026): Die Messung am Tag eines eingetragenen Check-Ups trägt die Marke
   „Kontrollmessung“ (`anlass`, `anlass_text`), der Block `kontrolle` zeigt den Vergleich seit dem letzten und seit dem
   ersten Check-Up — fertig vom Server, ohne Farben, Pfeile oder Richtungswörter. Eingetragen wird der Check-Up in der
   Karte „Check-Up“ (components/CheckupKarte.js). */

// Zeitstempel kommen in UTC: der Tag wird in Ortszeit des Studios gezeigt (nicht der UTC-Tag)
const am = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '');
const kcal = (v) => `${Number(v).toLocaleString('de-DE')} kcal`;
const knapp = (v) => String(v).replace('.', ',');
/* Eingabe -> Zahl. Leer = null (Feld nicht ausgefüllt). Alles, was keine reine Zahl ist („82,5 kg“, „17,8%“), ist ein
   Fehler am Feld — sonst ginge es als null zum Server: Pflichtfelder kämen mit einer nichtssagenden Meldung zurück,
   und der Grundumsatz (die einzige Probe gegen Tippfehler) fiele still weg. `ganz`: ganze Zahl, Tausenderpunkt und
   Leerzeichen erlaubt („1.826“, so schreibt die Karte ihn selbst). -> { wert } oder { fehler } */
export function leseZahl(v, { ganz = false, grenze, pflicht = false } = {}) {
  const roh = String(v ?? '').trim();
  if (!roh) return pflicht ? { fehler: 'Bitte ausfüllen.' } : { wert: null };
  const t = ganz ? roh.replace(/[.\s]/g, '') : roh.replace(',', '.');
  if (!(ganz ? /^\d+$/ : /^\d+(\.\d+)?$/).test(t)) return { fehler: ganz ? 'Bitte nur die Zahl, z. B. 1826' : 'Bitte nur die Zahl, z. B. 82,5' };
  const wert = Number(t);
  if (grenze && (wert < grenze.min || wert > grenze.max)) return { fehler: `${knapp(grenze.min)} bis ${knapp(grenze.max)}` };
  return { wert };
}
const bereich = (g) => (g ? `${knapp(g.min)}–${knapp(g.max)}` : '');
const text = (m, feld) => (m.zeilen || []).find((z) => z.feld === feld)?.text;
// Warum für dieses Mitglied nichts eingetragen werden kann (der Server liefert den Grund, die Sätze dort sind fürs Mitglied)
const GRUND = {
  kein_coaching: 'Nur für Mitglieder mit laufendem Ernährungscoaching (Einstellung „für“).',
  kein_mitglied: 'Keine laufende Mitgliedschaft und kein laufendes Ernährungscoaching.',
  unter_18: 'Unter 18 Jahren werden keine Messungen angenommen.',
  alter_unbekannt: 'Alter unbekannt: bitte das Geburtsdatum in der Akte oder das Alter im Kundenprofil eintragen (erst ab 18).',
};
const BEDINGUNG = [{ value: 'unbekannt', label: 'nicht gefragt' }, { value: 'nein', label: 'nein' }, { value: 'ja', label: 'ja' }];
const LEER = { gewicht: '', anteil: '', muskel: '', grundumsatz: '', groesse: '', datum: '', trainiert_12h: 'unbekannt', gegessen_3h: 'unbekannt', papier: false };

function Einwilligung({ e, nurLesen, onWiderruf }) {
  const widerruf = !nurLesen && <> · <button type="button" className="underline underline-offset-2 text-muted-foreground hover:text-foreground" onClick={onWiderruf}>Widerruf eintragen</button></>;
  if (e.liegt_vor) {
    return (
      <p className="text-sm">
        Einwilligung: {e.art === 'app' ? `in der App erteilt am ${am(e.erteilt_am)}` : `liegt auf Papier vor (bestätigt von ${e.bestaetigt_von || '–'} am ${am(e.erteilt_am)})`}
        {e.gegen_entwurf && <span className="text-amber-700"> · gegen den Entwurf des Textes</span>}
        {widerruf}
      </p>
    );
  }
  // Zugestimmt wurde einer früheren Fassung: Neues braucht die Zustimmung zur aktuellen, zurücknehmen lässt sie sich trotzdem
  if (e.frueher) {
    return (
      <p className="text-sm text-amber-700" role="status">
        Einwilligung zur früheren Fassung {e.frueher.version} ({e.frueher.art === 'papier' ? 'auf Papier' : 'in der App'}, {am(e.frueher.erteilt_am)}) — für neue Messungen braucht es die Zustimmung zur aktuellen Fassung {e.version}.
        {widerruf}
      </p>
    );
  }
  if (e.widerrufen_am) return <p className="text-sm text-amber-700" role="status">Einwilligung widerrufen am {am(e.widerrufen_am)} — es werden keine neuen Messungen angenommen.</p>;
  return <p className="text-sm text-muted-foreground">Noch keine Einwilligung des Mitglieds (Körperanalyse, Fassung {e.version}).</p>;
}

function Handeingabe({ leadId, ka, onNeu }) {
  const [f, setF] = useState(LEER);
  const [fehler, setFehler] = useState({});
  const [laeuft, setLaeuft] = useState(false);
  const setze = (k) => (ev) => { setF({ ...f, [k]: ev.target.value }); if (fehler[k]) setFehler({ ...fehler, [k]: undefined }); };
  const braucht_haken = !ka.einwilligung.liegt_vor;
  const g = ka.grenzen || {};
  const eintragen = async (ev) => {
    ev.preventDefault();
    // Jedes ausgefüllte Feld muss eine Zahl sein — gemeldet am Feld, nichts geht als „leer“ zum Server
    const gelesen = {
      gewicht: leseZahl(f.gewicht, { pflicht: true, grenze: g.gewicht_kg }), anteil: leseZahl(f.anteil, { pflicht: true, grenze: g.koerperfett_prozent }),
      muskel: leseZahl(f.muskel, { pflicht: true, grenze: g.skelettmuskel_kg }), grundumsatz: leseZahl(f.grundumsatz, { ganz: true, grenze: g.grundumsatz_kcal }),
      groesse: leseZahl(f.groesse, { grenze: g.groesse_cm }),
    };
    const neu = Object.fromEntries(Object.entries(gelesen).filter(([, r]) => r.fehler).map(([k, r]) => [k, r.fehler]));
    setFehler(neu);
    if (Object.keys(neu).length) return;
    setLaeuft(true);
    try {
      const { data } = await ernaehrungAPI.messungEintragen(leadId, {
        gewicht_kg: gelesen.gewicht.wert, koerperfett_prozent: gelesen.anteil.wert, skelettmuskel_kg: gelesen.muskel.wert,
        grundumsatz_kcal: gelesen.grundumsatz.wert, groesse_cm: gelesen.groesse.wert, datum: f.datum || null,
        bedingungen: { trainiert_12h: f.trainiert_12h, gegessen_3h: f.gegessen_3h }, einwilligung_papier: braucht_haken && f.papier,
      });
      if (data?.bekannt) toast.info('Diese Messung steht für den Tag schon da — nichts doppelt eingetragen.');
      else toast.success('Messung eingetragen');
      setF(LEER);
      onNeu();
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  const vollstaendig = f.gewicht && f.anteil && f.muskel && (!braucht_haken || f.papier);
  return (
    <form onSubmit={eintragen} className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Messung von Hand eintragen</p>
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Gewicht (kg)" required hint={bereich(g.gewicht_kg)} error={fehler.gewicht}><Input inputMode="decimal" value={f.gewicht} onChange={setze('gewicht')} aria-invalid={!!fehler.gewicht} /></FormField>
        <FormField label="Körperfett (%)" required hint={bereich(g.koerperfett_prozent)} error={fehler.anteil}><Input inputMode="decimal" value={f.anteil} onChange={setze('anteil')} aria-invalid={!!fehler.anteil} /></FormField>
        <FormField label="Skelettmuskelmasse (kg)" required hint={bereich(g.skelettmuskel_kg)} error={fehler.muskel}><Input inputMode="decimal" value={f.muskel} onChange={setze('muskel')} aria-invalid={!!fehler.muskel} /></FormField>
        <FormField label="Grundumsatz (kcal)" hint="freiwillig, als Prüfzahl" error={fehler.grundumsatz}><Input inputMode="numeric" value={f.grundumsatz} onChange={setze('grundumsatz')} aria-invalid={!!fehler.grundumsatz} /></FormField>
        <FormField label="Größe am Gerät (cm)" hint="freiwillig" error={fehler.groesse}><Input inputMode="decimal" value={f.groesse} onChange={setze('groesse')} aria-invalid={!!fehler.groesse} /></FormField>
        <FormField label="Gemessen am" hint="leer = jetzt"><Input type="date" value={f.datum} onChange={setze('datum')} /></FormField>
        {(ka.fragen || []).map((frage) => (
          <FormField key={frage.schluessel} label={frage.text}><NativeSelect value={f[frage.schluessel]} onChange={(v) => setF({ ...f, [frage.schluessel]: v })} options={BEDINGUNG} /></FormField>
        ))}
      </div>
      {braucht_haken && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={f.papier} onChange={(ev) => setF({ ...f, papier: ev.target.checked })} />
          <span>Die Einwilligung des Mitglieds zur Körperanalyse liegt auf Papier vor.{ka.einwilligung.widerrufen_am ? ' Achtung: Die frühere Einwilligung wurde widerrufen — nur ankreuzen, wenn es danach eine neue gibt.' : ''} <span className="text-muted-foreground">Dein Name und der Zeitpunkt werden dazu vermerkt.</span></span>
        </label>
      )}
      <p className="text-xs text-muted-foreground">Körperfett in kg und fettfreie Masse rechnet der Server aus Gewicht und Anteil. Passt der Grundumsatz nicht dazu, ist eine Zahl vertippt — ohne Grundumsatz gibt es diese Probe nicht.</p>
      <Button type="submit" size="sm" variant="outline" disabled={laeuft || !vollstaendig}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Eintragen</Button>
    </form>
  );
}

/* Block „Kontrollmessungen“: `k` ist `ka.kontrolle`. Je Vergleich eine kleine Tabelle (Titel, Zeilen mit Text und
   Vermerk, darunter der Hinweis zu den Messbedingungen) — gibt es noch keinen Vergleich, der Satz des Servers. */
function Kontrollmessungen({ k }) {
  if (!k || (!(k.anzahl > 0) && !k.text)) return null;       // älterer Server oder kein Check-Up
  const vergleiche = [k.seit_letztem, k.seit_erstem].filter(Boolean);
  return (
    <div data-testid="kontrollmessungen">
      <h3 className="text-sm font-semibold mb-1">Kontrollmessungen</h3>
      {k.kurz && <p className="text-xs text-muted-foreground">{k.kurz}</p>}
      {vergleiche.map((v) => (
        <div key={v.titel} className="mt-1.5">
          <table className="text-sm w-full">
            <caption className="text-left text-xs font-medium pb-0.5">{v.titel}</caption>
            <tbody>
              {v.zeilen.map((z) => (
                <tr key={z.feld} className="border-t align-top">
                  <td className="py-1 pr-2">{z.name}</td>
                  <td className="py-1 text-right tabular-nums whitespace-nowrap">{z.text}
                    {z.vermerk && <span className="block text-[11px] text-muted-foreground whitespace-normal">{z.vermerk}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {v.hinweis && <p className="text-xs text-amber-700" role="status">{v.hinweis}</p>}
        </div>
      ))}
      {vergleiche.length === 0 && k.text && <p className="text-sm text-muted-foreground">{k.text}</p>}
    </div>
  );
}

/* Karte am Mitglied. `ka`: Feld koerperanalyse aus GET /ernaehrung/mitglied/{id} bzw. der Trainer-Webseite;
   `grundumsatzRechnung`: der gerechnete Grundumsatz aus dem Kundenprofil (kann fehlen). */
export function KoerperanalyseKarte({ leadId, ka, grundumsatzRechnung, onNeu, nurLesen = false }) {
  const [alle, setAlle] = useState(false);
  if (!ka) return null;                                   // älterer Server während des Einspielens
  const m = ka.letzte;
  const liste = alle ? ka.messungen : ka.messungen.slice(0, 6);
  // Der gerechnete Grundumsatz steht direkt unter dem der Waage (fehlt die Zeile der Waage: am Ende)
  const zeilen = m ? m.zeilen.flatMap((z) => (z.feld === 'grundumsatz_kcal' ? [z, 'rechnung'] : [z])) : [];
  if (m && typeof grundumsatzRechnung === 'number' && !zeilen.includes('rechnung')) zeilen.push('rechnung');
  const mitVermerk = (m && m.zeilen.some((z) => z.vermerk)) || liste.some((x) => Object.keys(x.vermerke || {}).length > 0);
  const loeschen = async (messung) => {
    // Die Rückfrage kommt fertig vom Server — mit dem Satz, dass das Gewicht dieser Messung aus dem Gewichtsverlauf mitgeht
    if (!window.confirm(messung.loeschen_frage || `Messung vom ${messung.titel} löschen? Das lässt sich nicht zurücknehmen.`)) return;
    try { await ernaehrungAPI.messungLoeschen(leadId, messung.id); toast.success('Messung gelöscht'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); }
  };
  const bogen = async (messung) => {
    // Neues Fenster sofort öffnen (Popup-Sperre), die Datei danach hineinladen
    const fenster = window.open('', '_blank');
    try {
      const r = await ernaehrungAPI.koerperBogen(leadId, messung.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (fenster) fenster.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { if (fenster) fenster.close(); toast.error(fehlertext(err)); }
  };
  const inTresor = async (messung) => {
    if (!window.confirm(`Ergebnisbogen vom ${messung.titel} in den Tresor des Mitglieds legen? Mit Ernährungscoaching sieht es ihn dann in der App.`)) return;
    try { await ernaehrungAPI.koerperBogenTresor(leadId, messung.id); toast.success('Ergebnisbogen liegt im Tresor'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); }
  };
  const widerruf = async () => {
    if (!window.confirm('Widerruf der Einwilligung eintragen? Danach werden keine neuen Messungen angenommen. Vorhandene Messungen bleiben, bis sie gelöscht werden.')) return;
    try { await ernaehrungAPI.koerperWiderruf(leadId); toast.success('Widerruf eingetragen'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" aria-hidden="true" />Körperanalyse
        {!ka.aktiv && <Badge variant="outline" className="bg-muted text-muted-foreground">ausgeschaltet</Badge>}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {!ka.aktiv && <p className="text-sm text-muted-foreground" role="status">Die Körperanalyse (InBody) ist ausgeschaltet — es werden keine Messungen angenommen. Einschalten kann sie die Leitung unter Ernährung › Check-ins.</p>}
        {ka.aktiv && !ka.zugang.darf && <p className="text-sm text-muted-foreground" role="status">{GRUND[ka.zugang.grund] || ka.zugang.text}</p>}
        {(ka.aktiv || ka.einwilligung.liegt_vor || ka.einwilligung.frueher || ka.einwilligung.widerrufen_am) && <Einwilligung e={ka.einwilligung} nurLesen={nurLesen} onWiderruf={widerruf} />}
        {!m ? <p className="text-sm text-muted-foreground">Noch keine Messung.</p> : (
          <div className="space-y-1.5">
            <div className="text-sm"><strong>{m.titel}</strong> · {m.quelle_text}{m.geraet ? ` (InBody ${m.geraet.modell})` : ''}
              {m.anlass === 'checkup' && <Badge variant="outline" className="ml-1.5 font-normal bg-primary/10 text-primary border-primary/20">{m.anlass_text || 'Kontrollmessung'}</Badge>}
              {(m.vermerke_messung || []).map((v) => <Badge key={v} variant="outline" className="ml-1.5 font-normal bg-amber-500/10 text-amber-700 border-amber-200">{v}</Badge>)}</div>
            <table className="text-sm w-full">
              <caption className="sr-only">Letzte Messung</caption>
              <tbody>
                {zeilen.map((z) => (z === 'rechnung' ? (typeof grundumsatzRechnung === 'number' && (
                  <tr key="rechnung" className="border-t align-top"><td className="py-1 pr-2">Grundumsatz nach Formel (damit rechnet das Tagesziel)</td><td className="py-1 text-right tabular-nums whitespace-nowrap">{kcal(grundumsatzRechnung)}</td></tr>
                )) : (
                  <tr key={z.feld} className="border-t align-top">
                    <td className="py-1 pr-2">{z.name}{(m.abgeleitet || []).includes(z.feld) && <span className="text-muted-foreground"> (aus Gewicht und Anteil)</span>}</td>
                    <td className="py-1 text-right tabular-nums whitespace-nowrap">{z.text}
                      {(z.abstand || z.vermerk) && <span className="block text-[11px] text-muted-foreground whitespace-normal">{[z.abstand, z.vermerk].filter(Boolean).join(' · ')}</span>}</td>
                  </tr>
                )))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" onClick={() => bogen(m)}><FileText className="w-4 h-4 mr-1.5" aria-hidden="true" />Ergebnisbogen (PDF)</Button>
              {!nurLesen && <Button type="button" size="sm" variant="ghost" onClick={() => inTresor(m)}>In den Tresor des Mitglieds</Button>}
            </div>
            {(ka.abweichungen || []).map((a) => <p key={a} className="text-xs text-amber-700" role="status">{a} Gehört die Messung zu diesem Mitglied?</p>)}
          </div>
        )}
        {mitVermerk && ka.vermerk_erklaerung && <p className="text-xs text-muted-foreground">{ka.vermerk_erklaerung}</p>}
        <p className="text-xs text-muted-foreground">{ka.hinweis} Das Tagesziel rechnet weiter mit der Formel — der Grundumsatz der Waage ist eine zweite Schätzung, keine Messung.</p>
        <Kontrollmessungen k={ka.kontrolle} />
        {ka.messungen.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-1">Verlauf</h3>
            <ul className="divide-y text-sm">
              {liste.map((x) => (
                <li key={x.id} className="py-1.5 flex items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block">{x.titel} · <span className="text-muted-foreground">{x.quelle_text}</span></span>
                    {x.anlass === 'checkup' && <span className="block text-[11px] text-primary">{x.anlass_text || 'Kontrollmessung'}</span>}
                    <span className="block tabular-nums">{[text(x, 'gewicht_kg'), text(x, 'koerperfett_prozent'), text(x, 'skelettmuskel_kg') && `${text(x, 'skelettmuskel_kg')} Muskel`].filter(Boolean).join(' · ')}</span>
                    {Object.keys(x.vermerke || {}).length > 0 && <span className="block text-[11px] text-muted-foreground">Unterschied zur vorigen Messung innerhalb der Messunschärfe: {x.zeilen.filter((z) => z.vermerk).map((z) => z.name).join(', ')}</span>}
                    {(x.vermerke_messung || []).length > 0 && <span className="block text-[11px] text-amber-700">{x.vermerke_messung.join(', ')}</span>}
                  </span>
                  <span className="flex shrink-0">
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" aria-label={`Ergebnisbogen vom ${x.titel}`} onClick={() => bogen(x)}><FileText className="w-4 h-4" /></Button>
                    {!nurLesen && <Button type="button" size="icon" variant="ghost" className="h-7 w-7" aria-label={`Messung vom ${x.titel} löschen`} onClick={() => loeschen(x)}><Trash2 className="w-4 h-4" /></Button>}
                  </span>
                </li>
              ))}
            </ul>
            {ka.messungen.length > 6 && <Button type="button" size="sm" variant="ghost" onClick={() => setAlle(!alle)}>{alle ? 'Weniger zeigen' : `Alle ${ka.messungen.length} zeigen`}</Button>}
          </div>
        )}
        {!nurLesen && ka.aktiv && ka.zugang.darf && <Handeingabe leadId={leadId} ka={ka} onNeu={onNeu} />}
      </CardContent>
    </Card>
  );
}

/* Schalter für die Leitung (Ernährung › Check-ins): an/aus, „für“, Text und Version der Einwilligung. */
export function KoerperanalyseSchalter() {
  const [e, setE] = useState(null);
  const [f, setF] = useState(null);
  const [textOffen, setTextOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const uebernehmen = (d) => { setE(d); setF({ aktiv: d.aktiv, fuer: d.fuer, text: d.einwilligung.text, version: d.einwilligung.version, geprueft: !d.einwilligung.entwurf }); };
  useEffect(() => { ernaehrungAPI.koerperEinstellung().then((r) => uebernehmen(r.data)).catch(() => {}); }, []);
  if (!e || !f) return null;
  /* Ist das eine NEUE Fassung? Verglichen wird, wie der Server vergleicht (koerperanalyse.einstellung_setzen_sync):
     Text ohne Leerraum am Rand, Version mit zusammengezogenem Leerraum, ein leeres Feld heißt „unverändert“. Nur
     ein Zeilenumbruch am Ende ist keine neue Fassung — der Haken bliebe am Server stehen, die Karte dürfte ihn dann
     weder zurücksetzen noch „gilt als Entwurf“ melden. */
  const fassungNeu = (x) => {
    const text = (x.text || '').trim();
    const version = (x.version || '').trim().split(/\s+/).join(' ');
    return (!!text && text !== (e.einwilligung.text || '').trim()) || (!!version && version !== e.einwilligung.version);
  };
  const textNeu = fassungNeu(f);
  const pruefungNeu = f.geprueft !== !e.einwilligung.entwurf;
  /* Der Haken „anwaltlich geprüft“ gilt nur der GESPEICHERTEN Fassung: Mit jeder Änderung an Text oder Version fällt
     er zurück (sonst bestätigte ein Haken, der noch von der alten Fassung stand, die neue gleich mit). Die neue
     Fassung wird erst übernommen und dann in einem eigenen Schritt bestätigt — so hält es auch der Server. */
  const setzeFassung = (feld, wert) => setF((alt) => {
    const neu = { ...alt, [feld]: wert };
    return { ...neu, geprueft: fassungNeu(neu) ? false : !e.einwilligung.entwurf };
  });
  const darf = !!e.darf_schalten;                          // nur Leitung: alle anderen sehen die Karte, ohne etwas zu ändern
  const speichern = async () => {
    setLaeuft(true);
    const versionVorher = e.einwilligung.version;
    try {
      const { data } = await ernaehrungAPI.koerperEinstellungSetzen({
        aktiv: f.aktiv, fuer: f.fuer, ...(textNeu ? { einwilligung_text: f.text, einwilligung_version: f.version } : {}),
        // Eine neue Fassung ist wieder ein Entwurf: Der Haken wird nie im selben Schritt mitgeschickt
        ...(!textNeu && pruefungNeu ? { einwilligung_geprueft: f.geprueft } : {}),
      });
      uebernehmen(data);
      toast.success(data.aktiv ? 'Körperanalyse ist an' : 'Körperanalyse ist aus');
      // an der Antwort festgemacht: Eine neue Fassung trägt immer eine neue Version (der Server verlangt sie)
      if (data.einwilligung.version !== versionVorher) toast.info('Neue Fassung der Einwilligung gespeichert – sie gilt als Entwurf, bis du sie als anwaltlich geprüft bestätigst.');
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <Card>
      <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
        <span className="rounded-lg bg-primary/10 text-primary p-3 self-start"><Activity className="w-6 h-6" aria-hidden="true" /></span>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">Körperanalyse (InBody)</h3>
            <Badge variant="outline" className={e.aktiv ? 'bg-emerald-500/10 text-emerald-700 border-emerald-200' : ''}>{e.aktiv ? 'aktiv' : 'aus'}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">Mitglieder scannen nach der Gastmessung den Ergebnis-Code der Waage in der App oder tragen die Werte von Hand ein; Coaches können sie am Mitglied eintragen. Messwerte sind Gesundheitsdaten: Ohne Einwilligung wird nichts angenommen. Nur die Leitung schaltet.</p>
          {e.einwilligung.entwurf && <p className="rounded-md border border-amber-200 bg-amber-500/5 px-3 py-2 text-sm text-amber-800" role="status">Der Text der Einwilligung ist ein Entwurf — vor dem Einschalten vom Anwalt prüfen lassen, hier mit neuer Version eintragen und als geprüft bestätigen.</p>}
          {!darf && <p className="text-sm text-muted-foreground" role="status">Nur zum Lesen: Die Körperanalyse schaltet nur die Leitung.</p>}
          <div className="flex flex-wrap items-end gap-2">
            <FormField label="für"><NativeSelect className="w-80 max-w-full" disabled={!darf} value={f.fuer} onChange={(v) => setF({ ...f, fuer: v })} options={[{ value: 'alle', label: 'alle erwachsenen Mitglieder' }, { value: 'coaching', label: 'nur Mitglieder mit laufendem Ernährungscoaching' }]} /></FormField>
            <label className="flex items-center gap-2 text-sm pb-2"><Switch disabled={!darf} checked={f.aktiv} onCheckedChange={(v) => setF({ ...f, aktiv: v })} aria-label="Körperanalyse an" />{f.aktiv ? 'an' : 'aus'}</label>
            {darf && <Button disabled={laeuft} onClick={speichern}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Übernehmen</Button>}
            <Button type="button" variant="ghost" onClick={() => setTextOffen(!textOffen)}>{textOffen ? 'Text schließen' : `Einwilligung (Fassung ${e.einwilligung.version}${e.einwilligung.entwurf ? ', Entwurf' : ''})`}</Button>
          </div>
          {textOffen && (
            <div className="space-y-2 rounded-md border p-3">
              <FormField label="Version" hint="Ein geänderter Text braucht eine neue Version; danach fragt die App alle noch einmal. Eine frühere Version lässt sich nicht wieder einstellen."><Input className="w-48" maxLength={40} readOnly={!darf} value={f.version} onChange={(ev) => setzeFassung('version', ev.target.value)} /></FormField>
              <FormField label="Text der Einwilligung (so sieht ihn das Mitglied in der App)" hint={`Ohne Überschrift: Die App setzt „${e.einwilligung.titel}“ darüber.`}><Textarea rows={14} maxLength={6000} readOnly={!darf} value={f.text} onChange={(ev) => setzeFassung('text', ev.target.value)} /></FormField>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" disabled={!darf || textNeu} checked={f.geprueft} onChange={(ev) => setF({ ...f, geprueft: ev.target.checked })} />
                <span>Dieser Text ist in dieser Fassung anwaltlich geprüft. <span className="text-muted-foreground">Ohne den Haken gilt er als Entwurf, und jede Einwilligung wird als „gegen den Entwurf erteilt“ gekennzeichnet. Dein Name und der Zeitpunkt werden vermerkt.{e.einwilligung.geprueft_von ? ` Bestätigt von ${e.einwilligung.geprueft_von} am ${am(e.einwilligung.geprueft_am)}.` : ''}</span></span>
              </label>
              {textNeu && <p className="text-xs text-amber-700" role="status">Text oder Version sind geändert: Der Haken ist zurückgesetzt und gilt nicht für die neue Fassung. Erst „Übernehmen“, dann die neue Fassung hier als geprüft bestätigen.</p>}
              {e.geaendert_von && <p className="text-xs text-muted-foreground">Zuletzt geändert von {e.geaendert_von} am {am(e.geaendert_am)}.</p>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
